import React, { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import {
  CalendarBlank, Briefcase, Hammer, BookOpen, UsersThree, ChatCircleDots, GearSix, Compass, Path,
  SignOut, SunDim, MoonStars,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useAI } from "@/context/AIContext";
import LocationBadge from "@/components/LocationBadge";

// Items only meaningful once the user has an active Ruta.
// Explorar Rutas is intentionally NOT gated — a new user should be able to
// browse the catalog even before they own an active ruta.
const RUTA_GATED = new Set(["hoy", "mis-rutas", "mi-negocio", "construir", "clientes"]);

const NAV = [
  { id: "hoy",          label: "Hoy",                 path: "/",              Icon: CalendarBlank },
  { id: "mis-rutas",    label: "Mis Rutas",           path: "/mis-rutas",     Icon: Path },
  { id: "brujula",      label: "Brújula",             path: "/brujula",       Icon: Compass },
  { id: "mi-negocio",   label: "Mi Negocio",          path: "/negocio",       Icon: Briefcase },
  { id: "construir",    label: "Construir Mi Negocio", path: "/construir",    Icon: Hammer },
  { id: "explorar",     label: "Explorar Rutas",      path: "/explorar",      Icon: BookOpen },
  { id: "clientes",     label: "Clientes",            path: "/clientes",      Icon: UsersThree },
  { id: "ruta-ayuda",   label: "Ruta",                path: "/ruta",          Icon: ChatCircleDots },
  { id: "configuracion",label: "Configuración",       path: "/configuracion", Icon: GearSix },
];

/**
 * Lightweight hook: tracks whether the current user has any playbooks.
 * Re-fetches on route change so the sidebar reveals gated items as soon as
 * the user creates their first Ruta.
 */
function useHasRuta() {
  const { user } = useAuth();
  const location = useLocation();
  const [hasRuta, setHasRuta] = useState(true); // optimistic → avoids flicker on returning users
  useEffect(() => {
    if (!user) { setHasRuta(false); return; }
    let cancelled = false;
    api.get("/playbooks")
      .then((r) => { if (!cancelled) setHasRuta((r.data.playbooks || []).length > 0); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user, location.pathname]);
  return hasRuta;
}

const MOBILE_NAV = NAV.filter(n => ["hoy","mis-rutas","brujula","clientes","configuracion"].includes(n.id));

function ThemeBtn({ variant = "sidebar" }) {
  const { theme, toggle } = useTheme();
  return (
    <button
      onClick={toggle}
      data-testid={`toggle-tema-${variant}`}
      className="border-2 border-foreground p-2 hover:bg-foreground hover:text-background transition-colors"
      title={theme === "dark" ? "Cambiar a claro" : "Cambiar a oscuro"}
    >
      {theme === "dark" ? <SunDim size={18} weight="bold" /> : <MoonStars size={18} weight="bold" />}
    </button>
  );
}

export function Sidebar() {
  const { user, logout } = useAuth();
  const { openDrawer } = useAI();
  const hasRuta = useHasRuta();
  const visible = NAV.filter((n) => hasRuta || !RUTA_GATED.has(n.id));
  return (
    <aside
      className="hidden md:flex fixed left-0 top-0 h-screen w-[240px] border-r-2 border-foreground flex-col justify-between p-6 bg-background z-40"
      data-testid="sidebar"
    >
      <div>
        <div className="mb-10">
          <div className="display text-4xl leading-none">RUTA</div>
          <div className="label-mono mt-2 text-muted-foreground">Menos dudas. Más dirección.</div>
        </div>
        <nav className="flex flex-col gap-1">
          {visible.map((n) => (
            <NavLink
              key={n.id}
              to={n.path}
              end={n.path === "/"}
              data-testid={`nav-${n.id}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 border-2 transition-all ${
                  isActive
                    ? "border-foreground bg-foreground text-background"
                    : "border-transparent hover:border-foreground"
                }`
              }
            >
              <n.Icon size={18} weight="bold" />
              <span className="text-sm font-semibold">{n.label}</span>
            </NavLink>
          ))}
        </nav>

        <button
          onClick={() => openDrawer({ prefill: "" })}
          data-testid="btn-open-ai-sidebar"
          className="mt-6 w-full flex items-center justify-between gap-2 px-3 py-2.5 border-2 border-foreground bg-primary text-primary-foreground hover:opacity-90"
        >
          <span className="label-mono">Obtener Ayuda</span>
          <ChatCircleDots size={16} weight="bold" />
        </button>
      </div>

      <div className="flex flex-col gap-3">
        <div className="brut-card !p-3">
          <div className="label-mono text-muted-foreground">Sesión</div>
          <div className="text-sm font-semibold truncate mt-1" data-testid="sidebar-user-name">{user?.name}</div>
          <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
          <div className="mt-2">
            <LocationBadge variant="tag" testid="sidebar-location-badge" />
          </div>
        </div>
        <div className="flex items-center justify-between">
          <ThemeBtn variant="sidebar" />
          <button
            onClick={logout}
            data-testid="btn-logout"
            className="border-2 border-foreground p-2 hover:bg-foreground hover:text-background transition-colors"
            title="Cerrar sesión"
          >
            <SignOut size={18} weight="bold" />
          </button>
        </div>
      </div>
    </aside>
  );
}

export function MobileHeader() {
  const { openDrawer } = useAI();
  return (
    <header
      className="md:hidden sticky top-0 z-40 h-14 border-b-2 border-foreground bg-background flex items-center justify-between px-4"
      data-testid="mobile-header"
    >
      <div className="display text-2xl">RUTA</div>
      <div className="flex items-center gap-2">
        <ThemeBtn variant="mobile" />
        <button
          onClick={() => openDrawer({ prefill: "" })}
          data-testid="btn-open-ai-mobile"
          className="border-2 border-foreground p-2 bg-primary text-primary-foreground"
        >
          <ChatCircleDots size={18} weight="bold" />
        </button>
      </div>
    </header>
  );
}

export function MobileBottomNav() {
  const location = useLocation();
  const hasRuta = useHasRuta();
  const items = NAV.filter((n) => hasRuta || !RUTA_GATED.has(n.id));
  // Prefer the same order but capped to 5 for mobile bottom nav
  const preferredOrder = hasRuta
    ? ["hoy","mis-rutas","brujula","clientes","configuracion"]
    : ["brujula","ruta-ayuda","configuracion"];
  const mobileItems = preferredOrder
    .map((id) => items.find((n) => n.id === id))
    .filter(Boolean);
  return (
    <nav
      className="md:hidden fixed bottom-0 left-0 right-0 z-40 h-16 border-t-2 border-foreground bg-background grid"
      style={{ gridTemplateColumns: `repeat(${mobileItems.length}, minmax(0, 1fr))` }}
      data-testid="mobile-bottom-nav"
    >
      {mobileItems.map((n) => {
        const active = location.pathname === n.path;
        // Short label mapping — keeps the bottom nav readable on 320px viewports
        // where "Configuración" would otherwise overflow the cell.
        const shortLabel = ({
          "hoy": "Hoy",
          "mis-rutas": "Rutas",
          "brujula": "Brújula",
          "clientes": "Clientes",
          "configuracion": "Config",
          "ruta-ayuda": "Ruta",
        })[n.id] || n.label;
        return (
          <NavLink
            key={n.id}
            to={n.path}
            end={n.path === "/"}
            data-testid={`mobile-nav-${n.id}`}
            className={`flex flex-col items-center justify-center gap-1 px-1 border-r-2 border-foreground last:border-r-0 ${
              active ? "bg-foreground text-background" : ""
            }`}
          >
            <n.Icon size={18} weight="bold" />
            <span className="label-mono text-[10px] leading-none truncate max-w-full">{shortLabel}</span>
          </NavLink>
        );
      })}
    </nav>
  );
}
